import { Link } from "@tanstack/react-router";
import { Check, X } from "lucide-react";
import { useClaudeConnection } from "@/components/layout/ClaudeConnectionContext";
import { MetaText, ScoreValue, StatusPill } from "@/components/plan-audit/AuditPrimitives";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { DEFAULT_LIMIT } from "@/hooks/use-data-grid";
import { useRuns } from "@/hooks/use-plan-audit";
import { useSession } from "@/lib/auth-client";
import { formatDate, formatDuration } from "@/lib/plan-audit-format";

/**
 * Account — identity and execution, kept visibly separate.
 *
 * Microsoft SSO is who you are; the Claude connection is what a run executes on.
 * The two panels sit side by side because conflating them is the misunderstanding
 * this screen exists to prevent: signing in does not let you start an audit, and
 * connecting Claude does not grant access to anything.
 */
export function AccountView() {
  const { data: session } = useSession();
  const { connected, account, connectedAt, connect, disconnect } = useClaudeConnection();

  const runs = useRuns(
    { search: "", status: "", sort: "createdAt", sortDir: "desc" },
    { cursor: null, direction: "next" },
  );

  const user = session?.user;

  return (
    <>
      <div className="grid gap-4 xl:grid-cols-2">
        <div className="pa-card">
          <div className="pa-card-head">
            <h2>Signed in</h2>
            <span
              className="rounded-full px-2.5 py-1 text-[11px] font-semibold"
              style={{ background: "var(--success-bg)", color: "var(--success-fg)" }}
            >
              Microsoft SSO
            </span>
          </div>
          <FactRows
            rows={[
              { key: "Signed in as", value: user?.email ?? "—" },
              { key: "Name", value: user?.name ?? "—" },
              { key: "Provider", value: "Microsoft Azure AD" },
              {
                key: "Role",
                // Roles are granted in-app, not by an Azure group — worth stating
                // here because it is the question an admin always asks next.
                value: `${user?.role === "admin" ? "Power User" : "Auditor"} · set in-app`,
              },
            ]}
          />
          <p className="mt-3 text-xs leading-relaxed text-[var(--fg-3)]">
            Signing in grants read access to every solution, plan version, scorecard and flag
            already recorded. It does not let you start a run.
          </p>
        </div>

        <div
          className="pa-card"
          style={{ borderColor: connected ? "var(--success)" : "var(--border-1)" }}
        >
          <div className="pa-card-head">
            <h2>Claude connection</h2>
            <span
              className="flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold"
              style={{
                background: connected ? "var(--success-bg)" : "var(--bg-3)",
                color: connected ? "var(--success-fg)" : "var(--fg-2)",
              }}
            >
              <span
                className="size-1.5 rounded-full"
                style={{ background: connected ? "var(--success)" : "var(--fg-3)" }}
                aria-hidden="true"
              />
              {connected ? "Connected for this session" : "Not connected"}
            </span>
          </div>
          <FactRows
            rows={[
              { key: "Claude account", value: account ?? "—" },
              {
                key: "Connected at",
                value:
                  connectedAt === null
                    ? "Not connected"
                    : connectedAt.toLocaleTimeString("en-US", {
                        hour: "2-digit",
                        minute: "2-digit",
                      }),
              },
              // The strongest claim on the page, so it is stated as a fact row
              // rather than as prose someone can skip.
              { key: "Stored", value: "Never" },
            ]}
          />
          <div className="mt-3 flex items-center gap-3">
            {connected ? (
              <Button variant="outline" onClick={disconnect}>
                Disconnect
              </Button>
            ) : (
              <Button onClick={() => connect(user?.email ?? "unknown@nymbl.app")}>
                Connect Claude
              </Button>
            )}
            <MetaText>Session only — a page reload clears it</MetaText>
          </div>
        </div>
      </div>

      <div className="pa-card mt-4">
        <div className="pa-card-head">
          <h2>What you can do without connecting</h2>
        </div>
        <div className="flex flex-col gap-2">
          {[
            {
              allowed: true,
              text: "Read every solution, plan version, scorecard and flag already recorded.",
            },
            { allowed: true, text: "Read the Solution Plan Guide and its version history." },
            {
              allowed: false,
              text: "Start an audit run. Uploading a plan asks for a Claude connection first.",
            },
          ].map((rule) => (
            <div key={rule.text} className="flex items-start gap-2.5">
              <span
                className="mt-0.5 grid size-5 shrink-0 place-items-center rounded-full"
                style={{
                  background: rule.allowed ? "var(--success-bg)" : "var(--danger-bg)",
                  color: rule.allowed ? "var(--success-fg)" : "var(--danger-fg)",
                }}
                aria-hidden="true"
              >
                {rule.allowed ? (
                  <Check size={12} strokeWidth={3} />
                ) : (
                  <X size={12} strokeWidth={3} />
                )}
              </span>
              <span className="text-[13px] text-[var(--fg-2)]">{rule.text}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="pa-card mt-4">
        <div className="pa-card-head">
          <h2>Recent runs</h2>
          <MetaText>Most recent first</MetaText>
        </div>
        {runs.isLoading ? (
          <div className="flex flex-col gap-2">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-14 rounded-lg" />
            ))}
          </div>
        ) : runs.isError ? (
          <ErrorState message={runs.error.message} onRetry={() => void runs.refetch()} />
        ) : (runs.data?.data.length ?? 0) === 0 ? (
          <EmptyState title="No runs recorded" description="Audits you run will appear here." />
        ) : (
          <div className="flex flex-col gap-1">
            {(runs.data?.data ?? []).slice(0, DEFAULT_LIMIT).map((run) => (
              <Link
                key={run.id}
                to="/runs/$reference"
                params={{ reference: run.reference }}
                className="flex items-center gap-3 rounded-lg px-3 py-2.5 no-underline transition-colors hover:bg-[var(--bg-2)]"
              >
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[13px] font-semibold text-[var(--fg-1)]">
                    {run.solutionName} — {run.planVersionLabel}
                  </span>
                  <MetaText>
                    {run.reference} · {formatDate(run.startedAt)}
                    {run.durationSeconds !== null && ` · ${formatDuration(run.durationSeconds)}`}
                    {run.status === "failed" &&
                      run.failureReason.length > 0 &&
                      ` · ${run.failureReason}`}
                    {run.executedByEmail.length > 0 && ` · ${run.executedByEmail}`}
                  </MetaText>
                </span>
                <StatusPill status={run.status} />
                <ScoreValue score={run.overallScore} size="sm" />
              </Link>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

function FactRows({ rows }: { rows: { key: string; value: string }[] }) {
  return (
    <dl className="grid grid-cols-[130px_1fr] gap-x-3">
      {rows.map((row) => (
        <div
          key={row.key}
          className="col-span-2 grid grid-cols-subgrid border-b border-[var(--border-1)] py-2 last:border-0"
        >
          <dt className="pa-eyebrow">{row.key}</dt>
          <dd className="min-w-0 truncate text-[13px] font-medium text-[var(--fg-1)]">
            {row.value}
          </dd>
        </div>
      ))}
    </dl>
  );
}
